if (!customElements.get('products-toolbar')) {
  class ProductsToolbar extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    init() {
      this.filtersComponent = document.querySelector('facet-filters');
      this.filtersColumn = document.querySelector('.main-products-grid__filters');
      this.layoutSwitcher = this.querySelector('.products-toolbar__layout');
      this.compareToggle = document.getElementById('compare-switch');
      this.compareButton = document.getElementById('compare-drawer-open');
      this.filterBtn = this.querySelector('.products-toolbar__filter-btn');
      this.filterContainer = this.querySelector('.products-toolbar__filter');
      this.sortBy = this.querySelector('.products-toolbar__sort');
      this.productsGrid = document.querySelector('.main-products-grid');
      this.isMobile = window.matchMedia('(max-width: 768px)').matches;

      if (this.isMobile) {
        this.classList.add('btn', 'btn--primary', 'products-toolbar__mobile-filters-btn');

        // 👇 find pagination and set up IntersectionObserver
        this.paginationEl = document.querySelector('custom-pagination');

        if (this.paginationEl && 'IntersectionObserver' in window) {
          this.initMobileFiltersObserver();
        } else {
          // Fallback: always show on mobile if no pagination / no IO support
          this.classList.add('is-visible');
        }

        if (this.compareButton) {
          this.mountMobileCompareButton();
        }
      }

      if (this.filtersColumn) {
        this.filterToggle = this.querySelector('.js-toggle-filters');
        this.filterToggle.addEventListener('click', this.toggleFilters.bind(this));
      }

      if (this.layoutSwitcher) {
        this.layoutSwitcher.addEventListener('change', this.handleLayoutChange.bind(this));

        // Set the layout to the layout in localstorage
        const layout = theme.storageUtil.get('products-layout');
        if (layout && layout === 'list') {
          const toggle = document.getElementById(`${layout}-layout-opt`);
          if (toggle) toggle.click();
          this.handleLayoutChange(null, layout);
        }
      }

      if (this.compareToggle) {
        this.compareToggle.addEventListener('change', this.toggleCompare.bind(this));

        const isChecked = theme.storageUtil.get('compare-toggle', true);
        if (isChecked !== null) {
          this.toggleCompare(null, isChecked, true);
        } else {
          this.toggleCompare(null, this.compareToggle.checked, false);
        }

        setTimeout(() => {
          this.classList.add('transition-ready');
          this.productsGrid.classList.add('transition-ready');
        }, 500);
      } else if (theme.settings.compareToggle === 'none') {
        this.toggleCompare(null, true, false);
      }

      if (!this.filtersComponent && this.sortBy) {
        this.sortBy.addEventListener('change', ProductsToolbar.handleSortByChange);
      }
    }

    /**
     * Mobile-only: show button when it's "above" <custom-pagination>,
     * hide when pagination is in view.
     */
    initMobileFiltersObserver() {
      // Optional: adjust the rootMargin so the button hides only
      // when pagination is in the area where the button sits.
      const bottomOffset = parseInt(
        window.getComputedStyle(this).bottom.replace('px', ''),
        10
      ) || 0;

      const approximateBtnHeight = this.offsetHeight || 56; // fallback height
      const rootMarginBottom = -(bottomOffset + approximateBtnHeight + 16);

      this.mobileFiltersObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.classList.add('is-visible');
            } else {
              this.classList.remove('is-visible');
            }
          });
        },
        {
          root: null,
          threshold: 0,
          rootMargin: `0px 0px ${rootMarginBottom}px 0px`,
        }
      );

      this.mobileFiltersObserver.observe(this.paginationEl);
    }

    disconnectedCallback() {
      if (this.mobileFiltersObserver) {
        this.mobileFiltersObserver.disconnect();
      }
    }

    /**
     * Mobile-only: host the compare drawer button inside the filter slot
     * so it can swap with the filter button when compare is toggled on.
     */
    mountMobileCompareButton() {
      const host = this.filterContainer || this;

      if (!host.contains(this.compareButton)) {
        if (this.filterBtn) {
          this.filterBtn.after(this.compareButton);
        } else {
          host.prepend(this.compareButton);
        }
      }

      this.compareButton.classList.add('products-toolbar__compare-open');
      this.compareButton.removeAttribute('style');
    }

    /**
     * Updates the UI to reflect the compare state
     * @param {object} evt - Change event object
     * @param {boolean} checked - Whether compare is enabled or not (optional)
     * @param {boolean} persist - Whether to persist the new state (optional)
     */
    toggleCompare(evt, checked = null, persist = true) {
      const isChecked = evt ? evt.target.checked : checked;
      this.productsGrid.dataset.compare = isChecked;

      const compareButton = this.compareButton || document.getElementById('compare-drawer-open');

      if (this.isMobile) {
        this.classList.toggle('is-compare-active', isChecked);
        if (this.filterBtn) {
          this.filterBtn.hidden = isChecked;
        }
        if (compareButton) {
          compareButton.classList.toggle('is-out', !isChecked);
          if (isChecked) {
            compareButton.classList.remove('is-out');
            compareButton.removeAttribute('style');
          }
        }
      } else if (compareButton) {
        compareButton.classList.toggle('is-out', !isChecked);
      }

      if (!evt && this.compareToggle) this.compareToggle.checked = isChecked;

      if (persist && !Shopify.designMode) {
        theme.storageUtil.set('compare-toggle', isChecked);
      }

      // Load scripts/styles if not already loaded
      if (isChecked) {
        const resourcesTag = document.getElementById('compare-resources');
        if (resourcesTag) {
          const resources = JSON.parse(resourcesTag.textContent);
          resources.forEach((resource) => {
            if (resource.type === 'js') {
              const script = document.createElement('script');
              script.src = resource.path;
              document.head.appendChild(script);
            } else if (resource.type === 'css') {
              const link = document.createElement('link');
              link.href = resource.path;
              link.type = 'text/css';
              link.rel = 'stylesheet';
              document.head.appendChild(link);
            }
          });
          resourcesTag.remove();
        }
      }
    }

    /**
     * Toggles open/closed state of the filters on desktop.
     */
    toggleFilters() {
      this.filterToggle.classList.toggle('is-active');
      this.filtersOpen = this.filterToggle.classList.contains('is-active');
      this.filterToggle.setAttribute('aria-expanded', this.filtersOpen);

      if (theme.mediaMatches.lg) {
        this.filtersColumn.classList.toggle('lg:js-hidden', !this.filtersOpen);
        const productsList = document.querySelector('.main-products-grid__results > .grid');

        productsList.className = this.filtersOpen
          ? productsList.dataset.filtersOpenClasses
          : productsList.dataset.filtersClosedClasses;
      } else {
        this.filtersComponent.open();
      }
    }

    /**
     * Handles 'change' events on the layout switcher buttons.
     * @param {object} evt - Event object.
     * @param {string} layout - 'grid' or 'list' - the layout to change to.
     */
    handleLayoutChange(evt, layout) {
      this.productsGrid.dataset.layout = layout || evt.target.value;
      if (!layout) theme.storageUtil.set('products-layout', evt.target.value);
    }

    /**
     * Handles when a sort by dropdown is changed (and filtering is disabled)
     * @param {object} evt - Event object.
     */
    static handleSortByChange(evt) {
      const urlObj = new URL(window.location.href);
      urlObj.searchParams.set('sort_by', evt.detail.selectedValue);
      urlObj.hash = 'products-toolbar';
      window.location.href = urlObj.toString();
    }
  }

  customElements.define('products-toolbar', ProductsToolbar);
}
